import { useCallback, useState } from 'react';
import type { Result } from '@api';
import { Failure, Success, useErrorMessage, useDatahubAPI } from '@api';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import axios from 'axios';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useTranslation } from 'react-i18next';
import type { ManualOverrideTimestamp } from '@data/datahub/manualOverride/types';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useAtomCallback } from 'jotai/utils';

type APIResponse = {
  manual_overrides: ManualOverrideTimestamp[];
  page_size: number;
  next_page_token?: string;
};

const useRequest = () => {
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      params: URLSearchParams,
    ): Promise<Result<APIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/manual_overrides?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              manual_overrides: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  return request;
};

export const useManualOverrideTimestampsAPI = (): {
  loading: boolean;
  manualOverrideTimestamps: ManualOverrideTimestamp[];
  getManualOverrideTimestamps: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<void>;
} => {
  const [loading, setLoading] = useState(false);
  const [manualOverrideTimestamps, setManualOverrideTimestamps] = useState<
    ManualOverrideTimestamp[]
  >([]);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const request = useRequest();

  const getManualOverrideTimestamps = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        setManualOverrideTimestamps([]);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_manual_overrides', {
              status: 'failed',
            }),
          });
          setLoading(false);
          return;
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        const splitRequest = async (): Promise<void> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            // リクエストに失敗した場合
            notifyError({
              message: `${t('api.datahub.get_manual_overrides', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return;
          }
          // 取得成功時
          if (!!res && res.isSuccess()) {
            // 配列を結合
            setManualOverrideTimestamps((prevState) =>
              prevState.concat(
                res.value.manual_overrides.filter(
                  (data) =>
                    !isNullOrUndefined(data.start_timestamp) &&
                    data.start_timestamp !== '' &&
                    !isNullOrUndefined(data.end_timestamp) &&
                    data.end_timestamp !== '',
                ),
              ),
            );
            if (res.value.next_page_token) {
              // レスポンスに next_page がある場合は次のリクエストを行う
              if (params) {
                params.set('page_token', res.value.next_page_token);
              }
              await splitRequest();
            } else {
              setLoading(false);
            }
          }
        };

        await splitRequest();

        setLoading(false);
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    manualOverrideTimestamps,
    loading,
    getManualOverrideTimestamps,
  };
};

export const useSyncManualOverrideTimestampsAPI = (): {
  loading: boolean;
  getSyncManualOverrideTimestamps: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<ManualOverrideTimestamp[]>;
} => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const request = useRequest();

  const getSyncManualOverrideTimestamps = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_manual_overrides', {
              status: 'failed',
            }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        const splitRequest = async (
          data: ManualOverrideTimestamp[] = [],
        ): Promise<ManualOverrideTimestamp[]> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            // リクエストに失敗した場合
            notifyError({
              message: `${t('api.datahub.get_manual_overrides', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return data;
          }
          // 取得成功時
          if (res.isSuccess()) {
            // 配列を結合
            const combinedData = [
              ...data,
              ...res.value.manual_overrides.filter(
                (data) => data.start_timestamp && data.end_timestamp,
              ),
            ];
            if (isNullOrUndefined(res.value.next_page_token)) {
              return combinedData;
            }
            // レスポンスに next_page_token がある場合は次のリクエストを行う
            params.set('page_token', res.value.next_page_token);
            return await splitRequest(combinedData);
          }
          return data;
        };

        const data = await splitRequest();
        setLoading(false);
        return data;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    loading,
    getSyncManualOverrideTimestamps,
  };
};
